import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, TrendingDown, TrendingUp, Plus, X, Thermometer, Droplets, Wind, Cloud } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { API } from '../config/api';

const MultiSessionComparison = ({ races, onBack }) => {
  const [session1, setSession1] = useState(null);
  const [session2, setSession2] = useState(null);
  const [session1Drivers, setSession1Drivers] = useState([]);
  const [session2Drivers, setSession2Drivers] = useState([]);
  const [selectedPairs, setSelectedPairs] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [weather1, setWeather1] = useState(null);
  const [weather2, setWeather2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('comparison');

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(3);
    return `${mins}:${secs.padStart(6, '0')}`;
  };

  const getLastName = (fullName) => {
    const parts = fullName.trim().split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : fullName;
  };

  const loadSessionDrivers = async (sessionId, setDrivers) => {
    try {
      const response = await axios.get(`${API}/races/${sessionId}`);
      // Remove duplicates
      const uniqueDrivers = [];
      const seen = new Set();
      
      for (const driver of response.data.drivers) {
        if (!seen.has(driver.driver_number)) {
          seen.add(driver.driver_number);
          uniqueDrivers.push(driver);
        }
      }
      
      setDrivers(uniqueDrivers.sort((a, b) => a.driver_number - b.driver_number));
    } catch (error) {
      toast.error('Error al cargar pilotos');
    }
  };

  useEffect(() => {
    if (session1) {
      loadSessionDrivers(session1.id, setSession1Drivers);
    } else {
      setSession1Drivers([]);
    }
  }, [session1]);

  useEffect(() => {
    if (session2) {
      loadSessionDrivers(session2.id, setSession2Drivers);
    } else {
      setSession2Drivers([]);
    }
  }, [session2]);

  const addDriverPair = () => {
    setSelectedPairs([...selectedPairs, { session1_driver: null, session2_driver: null }]);
  };

  const removePair = (index) => {
    setSelectedPairs(selectedPairs.filter((_, i) => i !== index));
  };

  const updatePair = (index, field, value) => {
    const newPairs = [...selectedPairs];
    newPairs[index][field] = parseInt(value);
    setSelectedPairs(newPairs);
  };

  const handleCompare = async () => {
    if (!session1 || !session2) {
      toast.error('Selecciona ambas sesiones');
      return;
    }

    const validPairs = selectedPairs.filter(p => p.session1_driver && p.session2_driver);
    
    if (validPairs.length === 0) {
      toast.error('Selecciona al menos un par de pilotos');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/races/compare-sessions`, {
        session1_id: session1.id,
        session2_id: session2.id,
        driver_pairs: validPairs
      });
      
      setComparisonData(response.data);

      // Fetch weather for both sessions in parallel. If either call fails with
      // 422 (circuit unresolved), try the reanalyze-circuit endpoint once and
      // retry — that downloads the original Drive file and extracts the city
      // from its contents.
      const fetchWeather = async (sessionId) => {
        try {
          return (await axios.get(`${API}/races/${sessionId}/weather`)).data;
        } catch (err) {
          if (err.response?.status === 422) {
            try {
              await axios.post(`${API}/races/${sessionId}/reanalyze-circuit`);
              return (await axios.get(`${API}/races/${sessionId}/weather`)).data;
            } catch {
              return null;
            }
          }
          return null;
        }
      };
      const [w1, w2] = await Promise.all([
        fetchWeather(session1.id),
        fetchWeather(session2.id),
      ]);
      setWeather1(w1);
      setWeather2(w2);
    } catch (error) {
      toast.error('Error al comparar sesiones');
    } finally {
      setLoading(false);
    }
  };

  if (comparisonData) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <Card className="bg-neutral-900 border-red-900/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-white">Comparación Multi-Sesión</CardTitle>
                <CardDescription className="text-gray-400 mt-2">
                  {comparisonData.session1_name} ({comparisonData.session1_date}) vs {comparisonData.session2_name} ({comparisonData.session2_date})
                </CardDescription>
              </div>
              <Button onClick={() => setComparisonData(null)} variant="outline" className="border-red-600">
                Nueva Comparación
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Navigation Tabs */}
        <Card className="bg-neutral-900 border-red-900/30">
          <CardContent className="p-4">
            <div className="flex gap-2">
              <Button
                onClick={() => setViewMode('comparison')}
                variant={viewMode === 'comparison' ? 'default' : 'outline'}
                className={viewMode === 'comparison' ? 'bg-red-600 hover:bg-red-700' : 'border-red-600/50 text-gray-300'}
              >
                Comparación General
              </Button>
              <Button
                onClick={() => setViewMode('consistency')}
                variant={viewMode === 'consistency' ? 'default' : 'outline'}
                className={viewMode === 'consistency' ? 'bg-red-600 hover:bg-red-700' : 'border-red-600/50 text-gray-300'}
              >
                Análisis de Consistencia
              </Button>
              <Button
                onClick={() => setViewMode('lap-by-lap')}
                variant={viewMode === 'lap-by-lap' ? 'default' : 'outline'}
                className={viewMode === 'lap-by-lap' ? 'bg-red-600 hover:bg-red-700' : 'border-red-600/50 text-gray-300'}
              >
                Vuelta por Vuelta
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Weather comparison */}
        <WeatherComparisonPanel
          weather1={weather1} weather2={weather2}
          label1={comparisonData.session1_name}
          label2={comparisonData.session2_name}
          session1Id={session1.id}
          session2Id={session2.id}
          onRefresh={async () => {
            const [w1, w2] = await Promise.all([
              axios.get(`${API}/races/${session1.id}/weather`).then(r => r.data).catch(() => null),
              axios.get(`${API}/races/${session2.id}/weather`).then(r => r.data).catch(() => null),
            ]);
            setWeather1(w1);
            setWeather2(w2);
          }}
        />

        {/* Comparison View */}
        {viewMode === 'comparison' && comparisonData.comparisons.map((comp, idx) => {
          const isImprovement = comp.best_lap_delta < 0;

          // Compute a high-level verdict: how many metrics improved
          const metrics = [
            { key: 'Mejor vuelta', delta: comp.best_lap_delta },
            { key: 'Vuelta óptima', delta: comp.optimal_delta },
            { key: 'Ritmo promedio', delta: comp.avg_delta },
            { key: 'Consistencia', delta: comp.consistency_delta },
          ];
          const improvedMetrics = metrics.filter(m => m.delta < 0);
          const worsenedMetrics = metrics.filter(m => m.delta > 0);
          const verdict =
            improvedMetrics.length > worsenedMetrics.length ? 'session2' :
            improvedMetrics.length < worsenedMetrics.length ? 'session1' : 'draw';

          return (
            <Card key={idx} className="bg-neutral-900 border-red-900/30">
              <CardHeader>
                <CardTitle className="text-xl text-white">
                  <div className="flex items-center gap-2">
                    <span>#{comp.session1_driver_number} {getLastName(comp.session1_driver_name)}</span>
                    {comp.session1_driver_number !== comp.session2_driver_number && (
                      <span className="text-gray-400">vs #{comp.session2_driver_number} {getLastName(comp.session2_driver_name)}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 font-normal mt-1">
                    {comp.session1_driver_name} {comp.session1_driver_number !== comp.session2_driver_number && `→ ${comp.session2_driver_name}`}
                  </p>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Verdict banner */}
                <div
                  data-testid={`verdict-${idx}`}
                  className={`rounded-lg border p-4 ${
                    verdict === 'session2' ? 'bg-emerald-950/30 border-emerald-700/40' :
                    verdict === 'session1' ? 'bg-orange-950/30 border-orange-700/40' :
                    'bg-neutral-800/50 border-neutral-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Veredicto</p>
                      <p className={`text-base font-semibold ${
                        verdict === 'session2' ? 'text-emerald-300' :
                        verdict === 'session1' ? 'text-orange-300' :
                        'text-gray-200'
                      }`}>
                        {verdict === 'session2' && 'Mejoró en la sesión 2'}
                        {verdict === 'session1' && 'Rindió mejor en la sesión 1'}
                        {verdict === 'draw' && 'Rendimiento equivalente'}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        Ganó en <span className="text-emerald-400 font-semibold">{improvedMetrics.length}</span> de 4 métricas clave,
                        {' '}perdió en <span className="text-orange-400 font-semibold">{worsenedMetrics.length}</span>.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {metrics.map((m) => (
                        <span
                          key={m.key}
                          className={`text-[10px] px-2 py-0.5 rounded border ${
                            m.delta < 0 ? 'bg-emerald-600/20 border-emerald-500/30 text-emerald-300' :
                            m.delta > 0 ? 'bg-orange-600/20 border-orange-500/30 text-orange-300' :
                            'bg-neutral-700/50 border-neutral-600 text-gray-300'
                          }`}
                        >
                          {m.key} {m.delta < 0 ? '↓' : m.delta > 0 ? '↑' : '='}
                          {m.delta !== 0 && ` ${Math.abs(m.delta).toFixed(3)}s`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-neutral-800 border-neutral-700">
                    <CardContent className="p-4">
                      <p className="text-xs text-gray-400 mb-1">Mejor Vuelta</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg text-gray-300">{formatTime(comp.session1_best_lap)}</span>
                        <ArrowRight className="w-4 h-4 text-gray-500" />
                        <span className="text-lg font-bold text-white">{formatTime(comp.session2_best_lap)}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {isImprovement ? (
                          <TrendingDown className="w-4 h-4 text-green-500" />
                        ) : (
                          <TrendingUp className="w-4 h-4 text-red-500" />
                        )}
                        <span className={`text-sm font-semibold ${isImprovement ? 'text-green-500' : 'text-red-500'}`}>
                          {isImprovement ? '' : '+'}{comp.best_lap_delta.toFixed(3)}s ({isImprovement ? '' : '+'}{comp.best_lap_improvement_pct.toFixed(2)}%)
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-neutral-800 border-neutral-700">
                    <CardContent className="p-4">
                      <p className="text-xs text-gray-400 mb-1">
                        Vuelta Óptima
                        <span className="ml-1 text-xs text-gray-500">(suma mejores sectores)</span>
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg text-blue-400">{formatTime(comp.session1_optimal)}</span>
                        <ArrowRight className="w-4 h-4 text-gray-500" />
                        <span className="text-lg font-bold text-green-400">{formatTime(comp.session2_optimal)}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {comp.optimal_delta < 0 ? (
                          <TrendingDown className="w-4 h-4 text-green-500" />
                        ) : (
                          <TrendingUp className="w-4 h-4 text-red-500" />
                        )}
                        <span className={`text-sm font-semibold ${comp.optimal_delta < 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {comp.optimal_delta < 0 ? '' : '+'}{comp.optimal_delta.toFixed(3)}s
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-neutral-800 border-neutral-700">
                    <CardContent className="p-4">
                      <p className="text-xs text-gray-400 mb-1">Consistencia</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg text-gray-300">±{comp.session1_consistency.toFixed(3)}s</span>
                        <ArrowRight className="w-4 h-4 text-gray-500" />
                        <span className="text-lg font-bold text-white">±{comp.session2_consistency.toFixed(3)}s</span>
                      </div>
                      <div className="mt-2">
                        <span className={`text-sm ${comp.consistency_delta < 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {comp.consistency_delta < 0 ? 'Más consistente' : 'Menos consistente'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-neutral-800 border-neutral-700">
                    <CardContent className="p-4">
                      <p className="text-xs text-gray-400 mb-1">Promedio</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg text-gray-300">{formatTime(comp.session1_avg)}</span>
                        <ArrowRight className="w-4 h-4 text-gray-500" />
                        <span className="text-lg font-bold text-white">{formatTime(comp.session2_avg)}</span>
                      </div>
                      <div className="mt-2">
                        <span className={`text-sm ${comp.avg_delta < 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {comp.avg_delta < 0 ? '' : '+'}{comp.avg_delta.toFixed(3)}s
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Optimal Lap Breakdown */}
                {comp.sector_deltas.length > 0 && (
                  <Card className="bg-neutral-800 border-neutral-700">
                    <CardHeader>
                      <CardTitle className="text-base text-white">
                        Desglose Vuelta Óptima - {getLastName(comp.session1_driver_name)}
                      </CardTitle>
                      <CardDescription className="text-gray-400 text-sm">
                        Suma de los mejores tiempos por sector (valores absolutos)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-blue-400">Sesión 1 - {comparisonData.session1_date}</p>
                          {comp.sector_deltas.map((sector) => (
                            <div key={`s1-${sector.sector_number}`} className="flex justify-between items-center py-2 px-3 bg-neutral-900 rounded">
                              <span className="text-gray-300">Sector {sector.sector_number}</span>
                              <span className="text-white font-mono">{sector.session1_best.toFixed(3)}s</span>
                            </div>
                          ))}
                          <div className="flex justify-between items-center py-2 px-3 bg-blue-950/30 rounded border border-blue-600/50 mt-2">
                            <span className="text-blue-400 font-semibold">Total Óptimo</span>
                            <span className="text-blue-400 font-bold font-mono">{formatTime(comp.session1_optimal)}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-green-400">Sesión 2 - {comparisonData.session2_date}</p>
                          {comp.sector_deltas.map((sector) => (
                            <div key={`s2-${sector.sector_number}`} className="flex justify-between items-center py-2 px-3 bg-neutral-900 rounded">
                              <span className="text-gray-300">Sector {sector.sector_number}</span>
                              <span className={`font-mono font-semibold ${
                                sector.delta < 0 ? 'text-green-400' : sector.delta > 0 ? 'text-red-400' : 'text-white'
                              }`}>
                                {sector.session2_best.toFixed(3)}s
                                {sector.delta !== 0 && (
                                  <span className="text-xs ml-1">
                                    ({sector.delta < 0 ? '' : '+'}{sector.delta.toFixed(3)})
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                          <div className="flex justify-between items-center py-2 px-3 bg-green-950/30 rounded border border-green-600/50 mt-2">
                            <span className="text-green-400 font-semibold">Total Óptimo</span>
                            <span className="text-green-400 font-bold font-mono">{formatTime(comp.session2_optimal)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Sector Comparison */}
                {comp.sector_deltas.length > 0 && (
                  <>
                    <Card className="bg-neutral-800 border-neutral-700">
                      <CardHeader>
                        <CardTitle className="text-base text-white">
                          Delta por Sector - {getLastName(comp.session1_driver_name)}
                        </CardTitle>
                        <CardDescription className="text-gray-400 text-sm">
                          Positivo = más lento en Sesión 2 | Negativo = más rápido en Sesión 2
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={comp.sector_deltas.map(s => ({
                            ...s,
                            sectorLabel: `S${s.sector_number} - ${getLastName(comp.session1_driver_name)}`
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis 
                              dataKey="sectorLabel" 
                              stroke="#9ca3af"
                              angle={-15}
                              textAnchor="end"
                              height={70}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis stroke="#9ca3af" label={{ value: 'Delta (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                              formatter={(value, name) => {
                                if (name === 'delta') return [`${value > 0 ? '+' : ''}${value}s`, 'Delta'];
                                return [value, name];
                              }}
                              labelFormatter={(label) => label}
                            />
                            <Bar 
                              dataKey="delta" 
                              fill="#dc2626"
                              name="Delta"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Sector Details Table */}
                    <Card className="bg-neutral-800 border-neutral-700">
                      <CardHeader>
                        <CardTitle className="text-base text-white">
                          Tabla Detallada por Sectores - {getLastName(comp.session1_driver_name)}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-neutral-700">
                                <th className="text-left py-3 px-4 text-gray-400 font-medium">Sector</th>
                                <th className="text-center py-3 px-4 text-gray-400 font-medium">
                                  <div className="font-semibold text-blue-400">#{comp.session1_driver_number} {getLastName(comp.session1_driver_name)}</div>
                                  <span className="text-xs text-gray-500">Sesión 1 ({comparisonData.session1_date})</span>
                                </th>
                                <th className="text-center py-3 px-4 text-gray-400 font-medium">
                                  <div className="font-semibold text-green-400">#{comp.session2_driver_number} {getLastName(comp.session2_driver_name)}</div>
                                  <span className="text-xs text-gray-500">Sesión 2 ({comparisonData.session2_date})</span>
                                </th>
                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Delta</th>
                                <th className="text-center py-3 px-4 text-gray-400 font-medium">Mejora %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comp.sector_deltas.map((sector) => (
                                <tr key={sector.sector_number} className="border-b border-neutral-700/50 hover:bg-neutral-700/30">
                                  <td className="py-3 px-4 text-white font-medium">S{sector.sector_number}</td>
                                  <td className="py-3 px-4 text-center text-gray-300">{sector.session1_best.toFixed(3)}s</td>
                                  <td className="py-3 px-4 text-center text-white font-semibold">{sector.session2_best.toFixed(3)}s</td>
                                  <td className={`py-3 px-4 text-center font-bold ${sector.delta < 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {sector.delta < 0 ? '' : '+'}{sector.delta.toFixed(3)}s
                                  </td>
                                  <td className={`py-3 px-4 text-center font-medium ${sector.improvement_pct > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {sector.improvement_pct > 0 ? '+' : ''}{sector.improvement_pct.toFixed(2)}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Lap Times Evolution */}
                <Card className="bg-neutral-800 border-neutral-700">
                  <CardHeader>
                    <CardTitle className="text-base text-white">
                      Evolución de Tiempos - {getLastName(comp.session1_driver_name)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="lap"
                          type="number"
                          domain={[1, 'dataMax']}
                          stroke="#9ca3af"
                          label={{ value: 'Vuelta', position: 'insideBottom', offset: -5, fill: '#9ca3af' }}
                        />
                        <YAxis 
                          stroke="#9ca3af"
                          label={{ value: 'Tiempo (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          formatter={(value) => formatTime(value)}
                        />
                        <Legend />
                        <Line
                          data={comp.session1_lap_times.map((time, i) => ({ lap: i + 1, time }))}
                          type="monotone"
                          dataKey="time"
                          stroke="#3b82f6"
                          name={`S1 (${comparisonData.session1_date}) - ${getLastName(comp.session1_driver_name)}`}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                        <Line
                          data={comp.session2_lap_times.map((time, i) => ({ lap: i + 1, time }))}
                          type="monotone"
                          dataKey="time"
                          stroke="#22c55e"
                          name={`S2 (${comparisonData.session2_date}) - ${getLastName(comp.session2_driver_name)}`}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          );
        })}

        {/* Consistency Analysis View */}
        {viewMode === 'consistency' && (
          <div className="space-y-6">
            {comparisonData.comparisons.map((comp, idx) => (
              <Card key={idx} className="bg-neutral-900 border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-xl text-white">
                    <div>Consistencia: #{comp.session1_driver_number} {getLastName(comp.session1_driver_name)}</div>
                    <p className="text-sm text-gray-400 font-normal mt-1">{comp.session1_driver_name}</p>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Session 1 Stats */}
                    <Card className="bg-neutral-800 border-neutral-700">
                      <CardHeader>
                        <CardTitle className="text-base text-blue-400">
                          Sesión 1 - {comparisonData.session1_date}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-400">Desviación Estándar</p>
                          <p className="text-2xl font-bold text-white">±{comp.session1_consistency.toFixed(3)}s</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Rango</p>
                          <p className="text-lg text-gray-300">
                            {comp.session1_lap_times.length > 0 && 
                              `${(Math.max(...comp.session1_lap_times) - Math.min(...comp.session1_lap_times)).toFixed(3)}s`
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Total Vueltas</p>
                          <p className="text-lg text-gray-300">{comp.session1_lap_times.length}</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Session 2 Stats */}
                    <Card className="bg-neutral-800 border-neutral-700">
                      <CardHeader>
                        <CardTitle className="text-base text-green-400">
                          Sesión 2 - {comparisonData.session2_date}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-400">Desviación Estándar</p>
                          <p className="text-2xl font-bold text-white">±{comp.session2_consistency.toFixed(3)}s</p>
                          <p className={`text-sm mt-1 ${comp.consistency_delta < 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {comp.consistency_delta < 0 ? '↓ Mejoró' : '↑ Empeoró'} ({Math.abs(comp.consistency_delta).toFixed(3)}s)
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Rango</p>
                          <p className="text-lg text-gray-300">
                            {comp.session2_lap_times.length > 0 && 
                              `${(Math.max(...comp.session2_lap_times) - Math.min(...comp.session2_lap_times)).toFixed(3)}s`
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Total Vueltas</p>
                          <p className="text-lg text-gray-300">{comp.session2_lap_times.length}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Scatter comparison */}
                  <Card className="bg-neutral-800 border-neutral-700">
                    <CardHeader>
                      <CardTitle className="text-base text-white">Dispersión de Tiempos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis 
                            dataKey="lap"
                            stroke="#9ca3af"
                            label={{ value: 'Vuelta', position: 'insideBottom', offset: -5, fill: '#9ca3af' }}
                          />
                          <YAxis 
                            stroke="#9ca3af"
                            label={{ value: 'Tiempo (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                          />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                            formatter={(value) => formatTime(value)}
                          />
                          <Legend />
                          <Line
                            data={comp.session1_lap_times.map((time, i) => ({ lap: i + 1, time }))}
                            type="monotone"
                            dataKey="time"
                            stroke="#3b82f6"
                            name={`S1: ${comp.session1_driver_name}`}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                          <Line
                            data={comp.session2_lap_times.map((time, i) => ({ lap: i + 1, time }))}
                            type="monotone"
                            dataKey="time"
                            stroke="#22c55e"
                            name={`S2: ${comp.session2_driver_name}`}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Lap by Lap View */}
        {viewMode === 'lap-by-lap' && (
          <div className="space-y-6">
            {comparisonData.comparisons.map((comp, idx) => (
              <Card key={idx} className="bg-neutral-900 border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-xl text-white">
                    <div>Vuelta por Vuelta: #{comp.session1_driver_number} {getLastName(comp.session1_driver_name)}</div>
                    <p className="text-sm text-gray-400 font-normal mt-1">{comp.session1_driver_name}</p>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-700">
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Vuelta</th>
                          <th className="text-center py-3 px-4 text-gray-400 font-medium">
                            <div className="font-semibold text-blue-400">#{comp.session1_driver_number} {getLastName(comp.session1_driver_name)}</div>
                            <span className="text-xs text-gray-500">S1 ({comparisonData.session1_date})</span>
                          </th>
                          <th className="text-center py-3 px-4 text-gray-400 font-medium">
                            <div className="font-semibold text-green-400">#{comp.session2_driver_number} {getLastName(comp.session2_driver_name)}</div>
                            <span className="text-xs text-gray-500">S2 ({comparisonData.session2_date})</span>
                          </th>
                          <th className="text-center py-3 px-4 text-gray-400 font-medium">Delta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: Math.max(comp.session1_lap_times.length, comp.session2_lap_times.length) }).map((_, lapIdx) => {
                          const s1Time = comp.session1_lap_times[lapIdx];
                          const s2Time = comp.session2_lap_times[lapIdx];
                          const delta = s1Time && s2Time ? s2Time - s1Time : null;
                          
                          return (
                            <tr key={lapIdx} className="border-b border-neutral-700/50 hover:bg-neutral-800/50">
                              <td className="py-3 px-4 text-white font-medium">#{lapIdx + 1}</td>
                              <td className="py-3 px-4 text-center text-blue-400">
                                {s1Time ? formatTime(s1Time) : '-'}
                              </td>
                              <td className="py-3 px-4 text-center text-green-400">
                                {s2Time ? formatTime(s2Time) : '-'}
                              </td>
                              <td className={`py-3 px-4 text-center font-bold ${
                                delta === null ? 'text-gray-500' : 
                                delta < 0 ? 'text-green-500' : 'text-red-500'
                              }`}>
                                {delta !== null ? `${delta < 0 ? '' : '+'}${delta.toFixed(3)}s` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-2xl text-white">Comparación Multi-Sesión</CardTitle>
          <CardDescription className="text-gray-400">
            Compara el rendimiento de pilotos entre diferentes sesiones para analizar progreso
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Session Selection */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Session 1 */}
        <Card className="bg-neutral-900 border-red-900/30">
          <CardHeader>
            <CardTitle className="text-lg text-white">Sesión 1</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select onValueChange={(value) => setSession1(races.find(r => r.id === value))}>
              <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white">
                <SelectValue placeholder="Seleccionar sesión..." />
              </SelectTrigger>
              <SelectContent className="bg-neutral-800 border-neutral-700">
                {races.map(race => (
                  <SelectItem key={race.id} value={race.id} className="text-white hover:bg-neutral-700">
                    {race.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {session1 && (
              <Badge variant="outline" className="border-blue-600 text-blue-400">
                {session1.driver_count} pilotos - {new Date(session1.date).toLocaleDateString('es-ES')}
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* Session 2 */}
        <Card className="bg-neutral-900 border-red-900/30">
          <CardHeader>
            <CardTitle className="text-lg text-white">Sesión 2</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select onValueChange={(value) => setSession2(races.find(r => r.id === value))}>
              <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white">
                <SelectValue placeholder="Seleccionar sesión..." />
              </SelectTrigger>
              <SelectContent className="bg-neutral-800 border-neutral-700">
                {races.map(race => (
                  <SelectItem key={race.id} value={race.id} className="text-white hover:bg-neutral-700">
                    {race.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {session2 && (
              <Badge variant="outline" className="border-green-600 text-green-400">
                {session2.driver_count} pilotos - {new Date(session2.date).toLocaleDateString('es-ES')}
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Driver Pairing */}
      {session1 && session2 && (
        <Card className="bg-neutral-900 border-red-900/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white">Emparejar Pilotos</CardTitle>
              <Button onClick={addDriverPair} size="sm" className="bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Añadir Par
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedPairs.length === 0 ? (
              <p className="text-gray-400 text-center py-4">
                Haz clic en "Añadir Par" para comenzar a comparar pilotos
              </p>
            ) : (
              selectedPairs.map((pair, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-neutral-800 rounded-lg">
                  <Select 
                    onValueChange={(value) => updatePair(index, 'session1_driver', value)}
                    value={pair.session1_driver?.toString()}
                  >
                    <SelectTrigger className="flex-1 bg-neutral-900 border-neutral-700 text-white">
                      <SelectValue placeholder="Piloto Sesión 1..." />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {session1Drivers.map(driver => (
                        <SelectItem 
                          key={`s1-${driver.driver_number}`} 
                          value={driver.driver_number.toString()} 
                          className="text-white hover:bg-neutral-700"
                        >
                          #{driver.driver_number} {driver.driver_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <ArrowRight className="w-5 h-5 text-gray-500 flex-shrink-0" />

                  <Select 
                    onValueChange={(value) => updatePair(index, 'session2_driver', value)}
                    value={pair.session2_driver?.toString()}
                  >
                    <SelectTrigger className="flex-1 bg-neutral-900 border-neutral-700 text-white">
                      <SelectValue placeholder="Piloto Sesión 2..." />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {session2Drivers.map(driver => (
                        <SelectItem 
                          key={`s2-${driver.driver_number}`} 
                          value={driver.driver_number.toString()} 
                          className="text-white hover:bg-neutral-700"
                        >
                          #{driver.driver_number} {driver.driver_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button 
                    onClick={() => removePair(index)}
                    variant="ghost"
                    size="icon"
                    className="text-red-400 hover:text-red-300 hover:bg-red-950/30 flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Compare Button */}
      {session1 && session2 && selectedPairs.length > 0 && (
        <Button 
          onClick={handleCompare}
          disabled={loading}
          className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
        >
          {loading ? 'Comparando...' : 'Comparar Sesiones'}
        </Button>
      )}
    </div>
  );
};

export default MultiSessionComparison;

// ---------- Weather comparison sub-component ---------------------------------

const WeatherComparisonPanel = ({ weather1, weather2, label1, label2, session1Id, session2Id, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [manual1, setManual1] = useState('');
  const [manual2, setManual2] = useState('');

  const resolve = async (sessionId, freeText) => {
    setBusy(true);
    try {
      if (freeText) {
        await axios.patch(`${API}/races/${sessionId}/location`, {
          circuit: `Autódromo ${freeText}`,
          location: freeText,
        });
      } else {
        // Server-side reanalysis based on the stored text fields
        await axios.post(`${API}/races/${sessionId}/reanalyze-circuit`);
      }
      if (onRefresh) await onRefresh();
    } catch (e) {
      // noop — UI will stay in the same state and the user can type manually
      console.warn('resolve failed', e);
    } finally {
      setBusy(false);
    }
  };

  const metrics = [
    { key: 'temperature_c', label: 'Temp. ambiente', unit: '°C', icon: Thermometer, decimals: 1 },
    { key: 'track_temp_estimate_c', label: 'Temp. pista (est.)', unit: '°C', icon: Thermometer, decimals: 1 },
    { key: 'humidity_pct', label: 'Humedad', unit: '%', icon: Droplets, decimals: 0 },
    { key: 'wind_speed_kmh', label: 'Viento', unit: 'km/h', icon: Wind, decimals: 1 },
    { key: 'pressure_hpa', label: 'Presión', unit: 'hPa', icon: Cloud, decimals: 0 },
  ];

  const fmt = (v, d) => (v == null ? '—' : v.toFixed(d));

  const WeatherResolveRow = ({ which, weather, label, sessionId, manual, setManual }) => {
    if (weather) return null;
    return (
      <div className="rounded-md border border-dashed border-orange-600/40 bg-orange-950/10 p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <p className="text-xs text-orange-300">
              Sesión {which}: no se pudo detectar el autódromo automáticamente.
            </p>
            <p className="text-[11px] text-gray-500 mt-0.5 truncate max-w-md">
              {label}
            </p>
          </div>
          <button
            type="button"
            onClick={() => resolve(sessionId)}
            disabled={busy}
            className="text-[11px] text-white bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 px-3 py-1 rounded disabled:opacity-50"
          >
            Volver a intentar
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (manual.trim()) resolve(sessionId, manual.trim()); }}
          className="flex gap-2 mt-2"
        >
          <input
            type="text"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Escribí la ciudad o autódromo (ej: Paraná)"
            className="flex-1 bg-neutral-900 border border-neutral-700 text-gray-200 text-sm rounded px-2 py-1 focus:outline-none focus:border-red-500 placeholder:text-gray-600"
          />
          <button
            type="submit"
            disabled={busy || !manual.trim()}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs px-3 py-1 rounded"
          >
            Buscar
          </button>
        </form>
      </div>
    );
  };

  return (
    <Card data-testid="weather-comparison-panel" className="bg-neutral-900 border-red-900/30">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Cloud className="w-4 h-4 text-sky-400" /> Clima de las sesiones
        </CardTitle>
        <CardDescription className="text-gray-400 text-xs">
          Condiciones meteorológicas reales tomadas del día y hora de cada carrera.
          El clima afecta directamente la adherencia, el rendimiento del motor y la
          degradación de gomas — útil para contextualizar las diferencias de tiempos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Resolver UI when weather could not be fetched */}
        <WeatherResolveRow which={1} weather={weather1} label={label1} sessionId={session1Id} manual={manual1} setManual={setManual1} />
        <WeatherResolveRow which={2} weather={weather2} label={label2} sessionId={session2Id} manual={manual2} setManual={setManual2} />

        {/* Header with session labels and condition badges */}
        {(weather1 || weather2) && (
          <div className="grid grid-cols-3 gap-3 items-center">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Sesión 1</div>
              <div className="text-sm text-gray-300 truncate">{label1}</div>
              {weather1?.weather_condition && (
                <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30 text-[10px] mt-1">
                  {weather1.weather_condition}
                </Badge>
              )}
              {weather1 && (
                <div className="text-[10px] text-gray-500 mt-1">
                  {weather1.location || weather1.circuit || '—'}
                </div>
              )}
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Δ</div>
              <ArrowRight className="w-4 h-4 text-gray-600 mx-auto" />
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Sesión 2</div>
              <div className="text-sm text-gray-300 truncate">{label2}</div>
              {weather2?.weather_condition && (
                <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30 text-[10px] mt-1">
                  {weather2.weather_condition}
                </Badge>
              )}
              {weather2 && (
                <div className="text-[10px] text-gray-500 mt-1">
                  {weather2.location || weather2.circuit || '—'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rain flags */}
        {(weather1?.rained_during_session || weather2?.rained_during_session) && (
          <div className="rounded-md bg-blue-950/30 border border-blue-500/30 p-2 text-xs text-blue-200 flex items-center gap-2">
            <Cloud className="w-3 h-3 shrink-0" />
            <span>
              {weather1?.rained_during_session && weather2?.rained_during_session ?
                'Llovió en ambas sesiones' :
                weather1?.rained_during_session ?
                  'Llovió solo en la sesión 1' :
                  'Llovió solo en la sesión 2'}
            </span>
          </div>
        )}

        {/* Metric rows */}
        {(weather1 || weather2) && (
          <div className="rounded-lg border border-neutral-800 overflow-hidden divide-y divide-neutral-800">
            {metrics.map(({ key, label, unit, icon: Icon, decimals }) => {
              const v1 = weather1?.[key];
              const v2 = weather2?.[key];
              const delta = (v1 != null && v2 != null) ? (v2 - v1) : null;
              const deltaAbs = delta != null ? Math.abs(delta) : 0;
              const threshold = { '°C': 1.0, '%': 5, 'km/h': 3, 'hPa': 3 }[unit] || 1;
              const significant = delta != null && deltaAbs >= threshold;
              return (
                <div key={key} className="grid grid-cols-3 items-center px-3 py-2 gap-2">
                  <div className="flex items-center gap-2 text-gray-300 text-sm">
                    <Icon className="w-3.5 h-3.5 text-gray-500" />
                    <span>{label}</span>
                  </div>
                  <div className="text-center tabular-nums">
                    <span className="text-lg font-semibold text-white">{fmt(v1, decimals)}</span>
                    <span className="text-xs text-gray-500 ml-1">{unit}</span>
                    <ArrowRight className="w-3 h-3 text-gray-600 inline-block mx-1" />
                    <span className="text-lg font-semibold text-white">{fmt(v2, decimals)}</span>
                    <span className="text-xs text-gray-500 ml-1">{unit}</span>
                  </div>
                  <div className="text-right tabular-nums">
                    {delta == null ? (
                      <span className="text-gray-600 text-xs">—</span>
                    ) : (
                      <span className={`text-sm font-semibold ${
                        !significant ? 'text-gray-400' :
                        delta > 0 ? 'text-orange-400' : 'text-cyan-400'
                      }`}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(decimals)}{unit}
                        {significant && (
                          <span className="text-[10px] text-gray-500 ml-1 uppercase">
                            {unit === '°C' ? (delta > 0 ? 'más caluroso' : 'más fresco')
                             : unit === '%' ? (delta > 0 ? 'más húmedo' : 'más seco')
                             : unit === 'km/h' ? (delta > 0 ? 'más viento' : 'menos viento')
                             : unit === 'hPa' ? (delta > 0 ? '↑ presión' : '↓ presión') : ''}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
